import { normalizeUsPhoneToE164 } from "@/utils/phone";

const splitDefault = (value: string) =>
  value
    .split(/[,;]/)
    .map((entry) => entry.trim())
    .filter(Boolean);

/**
 * Update only the inherited destinations; preserve manually selected extras
 * and removals. `previous`/`next` may list several comma-separated defaults.
 */
export const syncRecipientDefault = (
  values: string[],
  previous: string,
  next: string,
  mode: "email" | "phone",
): string[] => {
  const key = (value: string) =>
    mode === "phone"
      ? (normalizeUsPhoneToE164(value) ?? value.trim())
      : value.trim().toLowerCase();
  const before = splitDefault(previous);
  const after = splitDefault(next);
  const beforeKeys = new Set(before.map(key));
  const afterKeys = new Set(after.map(key));
  if (
    beforeKeys.size === afterKeys.size &&
    [...beforeKeys].every((value) => afterKeys.has(value))
  )
    return values;
  if (before.length === 0 && values.length === 0) return after;
  // Pair each removed default with a new one (an edited address) and replace
  // it in place. A default the user removed stays removed with its successor.
  const removed = before.filter((value) => !afterKeys.has(key(value)));
  const added = after.filter((value) => !beforeKeys.has(key(value)));
  const replacements = new Map(
    removed.map((value, index) => [key(value), added[index]] as const),
  );
  const updated = values.flatMap((value) => {
    const normalized = key(value);
    if (!replacements.has(normalized)) return [value];
    const replacement = replacements.get(normalized);
    return replacement ? [replacement] : [];
  });
  updated.push(...added.slice(removed.length));
  const seen = new Set<string>();
  return updated.filter((value) => {
    const normalized = key(value);
    if (seen.has(normalized)) return false;
    seen.add(normalized);
    return true;
  });
};

/** Initial chip list from a comma-separated default. */
export const recipientDefaultToList = splitDefault;
